import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell, CalendarClock, Check, CheckCheck, Send, X } from 'lucide-react'
import {
  cancelNotification,
  createNotification,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from '@/shared/lib/api'
import { cn } from '@/shared/lib/cn'

const UNREAD_POLL_MS = 60_000

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [composeOpen, setComposeOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [scheduledFor, setScheduledFor] = useState('')
  const [sending, setSending] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const refreshUnread = useCallback(async () => {
    try {
      setUnread(await getUnreadCount())
    } catch {
      // Badge polling is best-effort; leave the last known count.
    }
  }, [])

  const refreshList = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      setNotifications(await getNotifications({ limit: 30 }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshUnread()
    const interval = setInterval(refreshUnread, UNREAD_POLL_MS)
    return () => clearInterval(interval)
  }, [refreshUnread])

  useEffect(() => {
    if (open) {
      refreshList()
      refreshUnread()
    }
  }, [open, refreshList, refreshUnread])

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleMarkRead = async (notification: AppNotification) => {
    if (notification.read_at || notification.status !== 'delivered') return
    const updated = await markNotificationRead(notification.id)
    setNotifications((rows) => rows.map((r) => (r.id === updated.id ? updated : r)))
    setUnread((c) => Math.max(0, c - 1))
  }

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead()
    const now = new Date().toISOString()
    setNotifications((rows) =>
      rows.map((r) =>
        r.status === 'delivered' && !r.read_at ? { ...r, read_at: now } : r
      )
    )
    setUnread(0)
  }

  const handleCancel = async (notification: AppNotification) => {
    const updated = await cancelNotification(notification.id)
    setNotifications((rows) => rows.map((r) => (r.id === updated.id ? updated : r)))
  }

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !body.trim() || sending) return
    setSending(true)
    setError(null)
    try {
      await createNotification({
        title: title.trim(),
        body: body.trim(),
        // datetime-local values are naive; the API requires an offset, so
        // convert via Date, which interprets the value in the local zone.
        ...(scheduledFor
          ? { scheduled_for: new Date(scheduledFor).toISOString() }
          : {}),
      })
      setTitle('')
      setBody('')
      setScheduledFor('')
      setComposeOpen(false)
      await Promise.all([refreshList(), refreshUnread()])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  const scheduled = notifications.filter((n) => n.status === 'scheduled')
  const inbox = notifications.filter((n) => n.status === 'delivered')

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'relative min-w-[44px] min-h-[44px] p-2 flex items-center justify-center rounded-lg border transition-all duration-200',
          open
            ? 'bg-white/10 border-white/10 text-white'
            : 'bg-white/5 hover:bg-white/10 border-white/5 hover:border-white/10 text-[#EEEEEE]/60 hover:text-[#EEEEEE]'
        )}
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold"
            aria-label={`${unread} unread notifications`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-[360px] max-w-[calc(100vw-24px)] bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="text-sm font-medium text-white">Notifications</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                  title="Mark all read"
                  aria-label="Mark all read"
                >
                  <CheckCheck size={14} />
                </button>
              )}
              <button
                onClick={() => setComposeOpen(!composeOpen)}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  composeOpen
                    ? 'text-white bg-white/10'
                    : 'text-white/40 hover:text-white hover:bg-white/10'
                )}
                title="New notification"
                aria-label="New notification"
              >
                <Send size={14} />
              </button>
            </div>
          </div>

          {composeOpen && (
            <form onSubmit={handleSend} className="px-4 py-3 border-b border-white/10 space-y-2">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Message"
                rows={2}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-none"
              />
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  aria-label="Schedule for"
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white/70 focus:outline-none focus:border-white/30 [color-scheme:dark]"
                />
                <button
                  type="submit"
                  disabled={!title.trim() || !body.trim() || sending}
                  className="px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? 'Sending…' : scheduledFor ? 'Schedule' : 'Send'}
                </button>
              </div>
            </form>
          )}

          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && (
              <div className="px-4 py-6 text-sm text-white/40 text-center">Loading…</div>
            )}
            {error && !isLoading && (
              <div className="px-4 py-3 text-sm text-red-400">{error}</div>
            )}
            {!isLoading && !error && scheduled.length === 0 && inbox.length === 0 && (
              <div className="px-4 py-6 text-sm text-white/40 text-center">
                No notifications yet
              </div>
            )}

            {scheduled.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide text-white/30">
                  Scheduled
                </div>
                {scheduled.map((n) => (
                  <div
                    key={n.id}
                    className="px-4 py-2.5 flex items-start gap-3 hover:bg-white/5 transition-colors"
                  >
                    <CalendarClock size={14} className="mt-0.5 shrink-0 text-white/40" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{n.title}</div>
                      <div className="text-xs text-white/50 truncate">{n.body}</div>
                      {n.scheduled_for && (
                        <div className="text-[11px] text-white/30 mt-0.5">
                          {formatTimestamp(n.scheduled_for)}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleCancel(n)}
                      className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-white/10 transition-colors"
                      title="Cancel"
                      aria-label={`Cancel scheduled notification ${n.title}`}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {inbox.length > 0 && (
              <div>
                {scheduled.length > 0 && (
                  <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide text-white/30">
                    Inbox
                  </div>
                )}
                {inbox.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleMarkRead(n)}
                    className={cn(
                      'w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-white/5 transition-colors',
                      !n.read_at && 'bg-white/[0.03]'
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1.5 w-2 h-2 rounded-full shrink-0',
                        n.read_at ? 'bg-transparent' : 'bg-blue-400'
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div
                        className={cn(
                          'text-sm truncate',
                          n.read_at ? 'text-white/60' : 'text-white'
                        )}
                      >
                        {n.title}
                      </div>
                      <div className="text-xs text-white/50 truncate">{n.body}</div>
                      <div className="text-[11px] text-white/30 mt-0.5">
                        {formatTimestamp(n.delivered_at ?? n.created_at)}
                      </div>
                    </div>
                    {!n.read_at && (
                      <Check size={13} className="mt-1 shrink-0 text-white/20" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
